import React, { useState, useEffect } from "react";
import { Plus, Video, XCircle } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import { useAuth } from "../../context/AuthContext";
import {
  committeeMeetings as api,
  type CommitteeMeeting,
  type ApiError,
} from "../../services/api";
import DataGrid from "../../components/DataGrid";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};

const INIT = {
  title: "",
  description: "",
  scheduledAt: "",
  durationMinutes: "30",
};

const CommitteeMeetingsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isAdmin = user?.role !== "RESIDENT" && user?.role !== "TENANT";

  const [items, setItems] = useState<CommitteeMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [cancelingId, setCancelingId] = useState<string | null>(null);

  useEffect(() => {
    if (!societyId) {
      setLoading(false);
      return;
    }
    api
      .list(societyId)
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [societyId]);

  const openAdd = () => {
    setFormError("");
    setForm(INIT);
    setShowForm(true);
  };
  const closeForm = () => {
    setShowForm(false);
    setForm(INIT);
    setFormError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true);
    setFormError("");
    try {
      const created = await api.create(societyId, {
        title: form.title,
        description: form.description || undefined,
        scheduledAt: new Date(form.scheduledAt).toISOString(),
        durationMinutes: Number(form.durationMinutes) || 30,
      });
      setItems((p) => [created, ...p]);
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to schedule meeting.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (m: CommitteeMeeting) => {
    if (!societyId) return;
    setCancelingId(m.id);
    try {
      const updated = await api.cancel(societyId, m.id);
      setItems((p) => p.map((x) => (x.id === m.id ? updated : x)));
    } catch {
      /* ignore */
    } finally {
      setCancelingId(null);
    }
  };

  const getMeetingEndTime = (m: CommitteeMeeting) => {
    const started = new Date(m.scheduledAt).getTime();
    return new Date(started + (m.durationMinutes || 0) * 60_000);
  };

  const isMeetingExpired = (m: CommitteeMeeting) =>
    m.status === "SCHEDULED" && getMeetingEndTime(m).getTime() <= Date.now();

  return (
    <PageLayout>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "var(--nx-text-primary)",
              letterSpacing: "-0.03em",
              marginBottom: 4,
            }}
          >
            Committee Meetings
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {items.length} scheduled video meetings
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={openAdd}
            className="nx-btn nx-btn-primary"
            style={{ gap: 6 }}
          >
            <Plus size={14} /> Schedule Meeting
          </button>
        )}
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            {
              key: "title",
              header: "Title",
              cellStyle: { fontWeight: 600 },
              render: (m: CommitteeMeeting) => m.title,
            },
            {
              key: "scheduledAt",
              header: "When",
              cellStyle: { color: "var(--nx-text-secondary)" },
              render: (m: CommitteeMeeting) =>
                new Date(m.scheduledAt).toLocaleString(),
            },
            {
              key: "durationMinutes",
              header: "Duration",
              render: (m: CommitteeMeeting) => `${m.durationMinutes} min`,
            },
            {
              key: "status",
              header: "Status",
              render: (m: CommitteeMeeting) => (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color:
                      m.status === "CANCELLED"
                        ? "#8B93B3"
                        : m.status === "COMPLETED"
                          ? "#22C55E"
                          : "#4F8CFF",
                  }}
                >
                  {m.status}
                </span>
              ),
            },
            {
              key: "join",
              header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (m: CommitteeMeeting) =>
                m.status === "SCHEDULED" ? (
                  <span
                    style={{
                      display: "inline-flex",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <a
                      href={isMeetingExpired(m) ? undefined : m.joinUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="nx-btn nx-btn-secondary"
                      style={{
                        padding: "4px 10px",
                        fontSize: 12,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        opacity: isMeetingExpired(m) ? 0.4 : 1,
                        pointerEvents: isMeetingExpired(m) ? "none" : undefined,
                      }}
                      aria-disabled={isMeetingExpired(m)}
                      title={
                        isMeetingExpired(m)
                          ? "Meeting has ended"
                          : "Join meeting"
                      }
                    >
                      <Video size={12} /> Join
                    </a>
                    {isAdmin && (
                      <button
                        onClick={() => handleCancel(m)}
                        disabled={cancelingId === m.id}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#EF4444",
                        }}
                        title="Cancel meeting"
                      >
                        <XCircle size={14} />
                      </button>
                    )}
                  </span>
                ) : null,
            },
          ]}
          rows={items}
          rowKey={(m) => m.id}
          loading={loading}
          emptyIcon={
            <Video size={32} style={{ color: "var(--nx-text-muted)" }} />
          }
          emptyText="No committee meetings scheduled."
        />
      </div>

      {showForm && (
        <Modal
          title="Schedule a Committee Meeting"
          submitLabel="Schedule"
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Title *</label>
            <input
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              required
              placeholder="Monthly Committee Meeting"
              style={inp}
              autoFocus
            />
          </div>
          <div>
            <label style={lbl}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              placeholder="Agenda"
              style={{ ...inp, minHeight: 60, resize: "vertical" as const }}
            />
          </div>
          <div>
            <label style={lbl}>Date &amp; Time *</label>
            <input
              type="datetime-local"
              value={form.scheduledAt}
              onChange={(e) =>
                setForm((p) => ({ ...p, scheduledAt: e.target.value }))
              }
              required
              style={inp}
            />
          </div>
          <div>
            <label style={lbl}>Duration (minutes)</label>
            <input
              type="number"
              value={form.durationMinutes}
              onChange={(e) =>
                setForm((p) => ({ ...p, durationMinutes: e.target.value }))
              }
              style={inp}
            />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default CommitteeMeetingsPage;
